import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { DollarSign, UserPlus, Loader2, Trash2, X, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAdsTab = ({ token }) => {
  const [adStats, setAdStats] = useState(null);
  const [adCampaigns, setAdCampaigns] = useState([]);
  const [adLoading, setAdLoading] = useState(false);
  const [showCreateAd, setShowCreateAd] = useState(false);
  const [newAd, setNewAd] = useState({
    name: '', broker_name: '', description: '', destination_url: '',
    image_url: '', placement: 'sidebar', cpc: 0.10, cpm: 1.00
  });

  useEffect(() => { fetchAdStats(); }, []);

  const fetchAdStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ads/analytics`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setAdStats(await res.json());
      const campRes = await fetch(`${API_URL}/api/ads/campaigns`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (campRes.ok) { const d = await campRes.json(); setAdCampaigns(d.campaigns || []); }
    } catch (err) { console.error('Error fetching ad stats:', err); }
  };

  const createAdCampaign = async () => {
    if (!newAd.name || !newAd.broker_name || !newAd.destination_url) { toast.error('Please fill required fields'); return; }
    setAdLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ads/campaigns`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newAd)
      });
      if (res.ok) {
        toast.success('Campaign created!');
        setShowCreateAd(false);
        setNewAd({ name: '', broker_name: '', description: '', destination_url: '', image_url: '', placement: 'sidebar', cpc: 0.10, cpm: 1.00 });
        fetchAdStats();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed to create campaign'); }
    } catch { toast.error('Error creating campaign'); }
    finally { setAdLoading(false); }
  };

  const toggleCampaignActive = async (id, isActive) => {
    try {
      const res = await fetch(`${API_URL}/api/ads/campaigns/${id}`, {
        method: 'PATCH', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !isActive })
      });
      if (res.ok) { toast.success(`Campaign ${isActive ? 'paused' : 'activated'}`); fetchAdStats(); }
    } catch { toast.error('Error updating campaign'); }
  };

  const deleteCampaign = async (id) => {
    if (!confirm('Delete this campaign?')) return;
    try {
      const res = await fetch(`${API_URL}/api/ads/campaigns/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Campaign deleted'); fetchAdStats(); }
    } catch { toast.error('Error deleting campaign'); }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800 mb-8" data-testid="admin-ads-tab">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <DollarSign className="h-6 w-6 text-yellow-500" />
            <h3 className="text-lg font-semibold text-white">Advertising & League Sponsorship</h3>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center"><p className="text-xl font-bold text-white">{adStats?.campaigns?.total || 0}</p><p className="text-xs text-slate-400">Campaigns</p></div>
            <div className="text-center"><p className="text-xl font-bold text-green-400">{adStats?.campaigns?.active || 0}</p><p className="text-xs text-slate-400">Active</p></div>
            <div className="text-center"><p className="text-xl font-bold text-blue-400">{adStats?.totals?.impressions || 0}</p><p className="text-xs text-slate-400">Impressions</p></div>
            <div className="text-center"><p className="text-xl font-bold text-purple-400">{adStats?.totals?.clicks || 0}</p><p className="text-xs text-slate-400">Clicks</p></div>
            <Button onClick={() => setShowCreateAd(true)} className="bg-yellow-600 hover:bg-yellow-500">
              <UserPlus className="h-4 w-4 mr-1" /> New Campaign
            </Button>
          </div>
        </div>
        <p className="text-slate-400 text-sm mb-4">Manage broker advertising campaigns and league sponsorships.</p>

        {showCreateAd && (
          <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <h4 className="text-white font-medium mb-4">Create New Campaign</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400">Campaign Name *</label>
                <Input value={newAd.name} onChange={(e) => setNewAd(a => ({ ...a, name: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="Q1 2026 Promotion" />
              </div>
              <div>
                <label className="text-sm text-slate-400">Broker Name *</label>
                <Input value={newAd.broker_name} onChange={(e) => setNewAd(a => ({ ...a, broker_name: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="eToro" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-slate-400">Description</label>
                <Input value={newAd.description} onChange={(e) => setNewAd(a => ({ ...a, description: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-slate-400">Destination URL *</label>
                <Input value={newAd.destination_url} onChange={(e) => setNewAd(a => ({ ...a, destination_url: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              <div>
                <label className="text-sm text-slate-400">Image URL</label>
                <Input value={newAd.image_url} onChange={(e) => setNewAd(a => ({ ...a, image_url: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              <div>
                <label className="text-sm text-slate-400">Placement</label>
                <select value={newAd.placement} onChange={(e) => setNewAd(a => ({ ...a, placement: e.target.value }))} className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white">
                  <option value="sidebar">Sidebar</option>
                  <option value="banner">Banner</option>
                  <option value="inline">Inline</option>
                  <option value="leaderboard">Leaderboard</option>
                  <option value="league_sponsor">League Sponsor</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400">CPC ($)</label>
                <Input type="number" step="0.01" value={newAd.cpc} onChange={(e) => setNewAd(a => ({ ...a, cpc: parseFloat(e.target.value) }))} className="bg-slate-800 border-slate-700" />
              </div>
              <div>
                <label className="text-sm text-slate-400">CPM ($)</label>
                <Input type="number" step="0.01" value={newAd.cpm} onChange={(e) => setNewAd(a => ({ ...a, cpm: parseFloat(e.target.value) }))} className="bg-slate-800 border-slate-700" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowCreateAd(false)}>Cancel</Button>
              <Button onClick={createAdCampaign} disabled={adLoading} className="bg-yellow-600 hover:bg-yellow-500">
                {adLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create Campaign
              </Button>
            </div>
          </div>
        )}

        {adCampaigns.length > 0 && (
          <div className="bg-slate-800/50 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2 px-4 text-slate-400 text-sm font-medium">Campaign</th>
                  <th className="text-left py-2 px-4 text-slate-400 text-sm font-medium">Broker</th>
                  <th className="text-left py-2 px-4 text-slate-400 text-sm font-medium">Placement</th>
                  <th className="text-right py-2 px-4 text-slate-400 text-sm font-medium">Impressions</th>
                  <th className="text-right py-2 px-4 text-slate-400 text-sm font-medium">Clicks</th>
                  <th className="text-right py-2 px-4 text-slate-400 text-sm font-medium">CTR</th>
                  <th className="text-center py-2 px-4 text-slate-400 text-sm font-medium">Status</th>
                  <th className="text-center py-2 px-4 text-slate-400 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {adCampaigns.map((c) => {
                  const imp = c.stats?.impressions || 0;
                  const clk = c.stats?.clicks || 0;
                  const ctr = imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
                  return (
                    <tr key={c.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-2 px-4">
                        <span className="font-medium text-white">{c.name}</span>
                        {c.sponsored_leagues?.length > 0 && <Badge className="ml-2 bg-purple-500/20 text-purple-400 text-xs">{c.sponsored_leagues.length} leagues</Badge>}
                      </td>
                      <td className="py-2 px-4 text-slate-300">{c.broker_name}</td>
                      <td className="py-2 px-4"><Badge variant="outline" className="text-xs border-slate-600">{c.placement}</Badge></td>
                      <td className="py-2 px-4 text-right text-slate-300">{imp.toLocaleString()}</td>
                      <td className="py-2 px-4 text-right text-slate-300">{clk.toLocaleString()}</td>
                      <td className="py-2 px-4 text-right text-blue-400">{ctr}%</td>
                      <td className="py-2 px-4 text-center">
                        <Badge className={c.is_active ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>{c.is_active ? 'Active' : 'Paused'}</Badge>
                      </td>
                      <td className="py-2 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => toggleCampaignActive(c.id, c.is_active)} className="h-7 px-2">
                            {c.is_active ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteCampaign(c.id)} className="h-7 px-2 text-red-400 hover:text-red-300">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {adCampaigns.length === 0 && !showCreateAd && (
          <div className="text-center py-8 text-slate-400">
            <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No advertising campaigns yet</p>
            <p className="text-sm">Create a campaign to start showing broker ads</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
